import {Injectable, PLATFORM_ID, inject} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {SSR_OPTIONAL} from '../../app/interceptors/ssr-unavailable.interceptor';
import {isPlatformBrowser} from '@angular/common';
import {Observable, of, shareReplay, timer, switchMap, map} from 'rxjs';
import {environment} from '../../environments/environment.development';
import {controllersNames} from '../../app/controllersNames';

/** Монета из CoinGecko /coins/markets (vs_currency=rub). */
export interface CryptoCoin {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  market_cap: number;
  market_cap_rank: number;
  total_volume: number;
  high_24h: number;
  low_24h: number;
  price_change_24h: number | null;
  price_change_percentage_24h: number | null;
  /** просить ?price_change_percentage=24h,7d */
  price_change_percentage_7d_in_currency?: number | null;
  circulating_supply: number;
  total_supply: number | null;
  max_supply: number | null;
  ath: number;
  atl: number;
  last_updated: string;
}

/** Фиатный курс ЦБ РФ (daily_json.js → Valute). */
export interface FiatRate {
  code: string;
  name: string;
  nominal: number;
  value: number;
  previous: number;
  /** value - previous в % */
  changePct: number;
}

export type ChartPoint = { t: number; p: number };

/** Краткая новость — заголовок, сниппет и ссылка на оригинал. */
export interface CryptoNewsItem {
  title: string;
  link?: string;
  description?: string;
  source: string;
  publishedAtUtc?: string;
}

/** Деталка монеты: рынок + описание проекта, ссылки и апдейты. */
export interface CoinDetail {
  market?: CryptoCoin;
  description?: string;
  homepageUrl?: string;
  explorerUrl?: string;
  updates: CryptoNewsItem[];
}

/**
 * Курсы крипты и новости через собственный /api/crypto (двухуровневый
 * серверный кеш + fallback на снапшот в БД) и фиатные курсы ЦБ РФ
 * напрямую из браузера.
 */
/** Crypto data comes from external CoinGecko: its outage is not our overload — never 503 the page (#1174). */
const optional = () => new HttpContext().set(SSR_OPTIONAL, true);

@Injectable({providedIn: 'root'})
export class CryptoService {
  private http = inject(HttpClient);


  // запросы идут через наш бэкенд с двухуровневым кешем — лимиты
  // CoinGecko (10–30 req/min по IP) больше не жгутся каждым посетителем
  private readonly api = environment.apiBase + '/' + controllersNames.crypto;
  private readonly cbr = 'https://www.cbr-xml-daily.ru/daily_json.js';

  /** интервал фонового обновления таблицы */
  private readonly refreshMs = 120_000;
  /**
   * На сервере — разовый запрос: повторяющийся таймер не давал бы
   * приложению стабилизироваться и вешал бы SSR-рендер.
   */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** кеш по perPage: тикер просит 6, индекс — 100, общий один observable ломал оба */
  private markets$ = new Map<number, Observable<CryptoCoin[]>>();
  private fiat$?: Observable<FiatRate[]>;

  /**
   * Топ монет по капитализации в ₽. shareReplay — все подписчики
   * делят один HTTP-запрос; таймер обновляет раз в refreshMs.
   */
  getTop(perPage = 100): Observable<CryptoCoin[]> {
    let markets$ = this.markets$.get(perPage);
    if (!markets$) {
      markets$ = (this.isBrowser ? timer(0, this.refreshMs) : of(0)).pipe(
        switchMap(() => this.http.get<CryptoCoin[]>(`${this.api}/markets`, {
          params: {perPage}, context: optional()
        })),
        shareReplay({bufferSize: 1, refCount: true})
      );
      this.markets$.set(perPage, markets$);
    }
    return markets$;
  }

  /** Деталка монеты: рынок + описание + ссылки + апдейты проекта. */
  getCoinDetail(id: string): Observable<CoinDetail> {
    return this.http.get<CoinDetail>(
      `${this.api}/${encodeURIComponent(id)}`, {context: optional()});
  }

  /** История цены за N дней → точки для графика (бэк уже мапит в {t,p}). */
  getMarketChart(id: string, days = 7): Observable<ChartPoint[]> {
    return this.http.get<ChartPoint[]>(
      `${this.api}/${encodeURIComponent(id)}/chart`, {params: {days}, context: optional()});
  }

  /** Последние крипто-новости (сниппеты + ссылки на источники). */
  getNews(): Observable<CryptoNewsItem[]> {
    return this.http.get<CryptoNewsItem[]>(`${this.api}/news`, {context: optional()});
  }

  /** Фиатные курсы ЦБ РФ (раз в сутки обновляются — кэшируем агрессивно). */
  getFiat(): Observable<FiatRate[]> {
    interface CbrValute {
      CharCode: string; Name: string; Nominal: number; Value: number; Previous: number;
    }

    this.fiat$ ??= this.http.get<{ Valute: Record<string, CbrValute> }>(this.cbr).pipe(
      map(r => Object.values(r.Valute).map(v => {
        const value = v.Value / v.Nominal;
        const previous = v.Previous / v.Nominal;
        return <FiatRate>{
          code: v.CharCode,
          name: v.Name,
          nominal: v.Nominal,
          value,
          previous,
          changePct: previous ? (value - previous) / previous * 100 : 0,
        };
      })),
      shareReplay({bufferSize: 1, refCount: true})
    );
    return this.fiat$;
  }
}
